import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, FileText } from 'lucide-react';
import GroupedEngagementPicker from '../components/workflow/GroupedEngagementPicker';
import { EngagementBlockers, engagementBlockReasons } from '../components/workflow/RecordBlockers';
import { shortEngagementName, engagementLabelsOf } from '../components/workflow/CoverageDisplay';
import { CompanyTabName } from '../components/workflow/CompanyTabBar';
import {
  companyTabBar, companyTab, companyTabActive, companyTabIdle,
} from '../components/workflow/constants';
import { Field, FilePicker, InfoTip, inputCls, lockedInputCls, LockTip } from '../components/shared/FormKit';
import { FormValidationProvider, useFormValidation, FieldInvalidInline } from '../components/shared/formValidation';
import { FormShell, FormSection, SubSection } from '../components/workflow/FormShell';
import ReviewerSelect, { useIsQmReviewer } from '../components/workflow/ReviewerSelect';
import {
  emptyEngagementFields, engagementToFields, fieldsToEngagementValues,
  EngagementDateFields, EngagementProfileFields, EngagementPeopleFields,
  EAR_PEOPLE_HINT,
} from '../components/shared/EngagementFormFields';
import {
  emptyScreeningAnswers, screeningRequiredGaps, appendScreeningAnswers,
} from '../components/shared/ScreeningFields';
import { ScreeningUpload, ENGREG_REVIEWER_HINT } from './WorkflowEngRegNew';
import { earSlotConflict } from './earSlotConflict';
import {
  useEngagementSlotCheck, DuplicateEngagementWarning, DescriptionOfServiceNotice, descriptionNeededFrom,
} from '../components/shared/EngagementCompanies';
import { fetchJSON } from '../utils/api';

// Engagement Amendment — submission form (docs/modules/workflow-amendment.md).
// UPDATE-ONLY: CaseWare access / charge codes belong to the
// CaseWare & Charge Code flow, and the QC link is the QC flow's job.
//
// The editor renders the SHARED engagement field set
// (shared/EngagementFormFields.js) — the same blocks Engagement Registration and
// Management → New engagement use, so a field exists on all three or on none.
// Nothing is marked required here: an amendment changes what it wants to
// change, and the "at least one Manager / exactly one Partner" rules are
// checked against the resulting change set instead.

// Engagement fields an update request can carry (sign-off / archival fields are
// deliberately absent — they belong to the FS flow and archival management;
// engagementName is composed from Entity + UEN + FYE; status is maintained by
// the flows themselves and is changed through Engagement Status Change).
export const EMPTY_EAR_FIELDS = {
  ...emptyEngagementFields(),
  clientGroup: '', entityId: null,
};

// Live engagement → the diff baseline, in the engagement master's value shape
// (the same shape fieldsToEngagementValues produces, so the two are comparable).
export const engagementToEarValues = (en) => ({
  ...fieldsToEngagementValues(engagementToFields(en || {}), {}),
  serviceProvided: (en && en.serviceProvided) || null,
  clientGroup: (en && en.clientGroup) || null,
  entityId: (en && en.entityId) != null ? en.entityId : null,
});

const ID_LIST_FIELDS = ['teamStaffIds', 'partnerStaffIds', 'directorStaffIds', 'managerStaffIds'];
// Answered once for the whole request in its own Screening section, never
// per engagement (the backend rejects a per-engagement change).
const NOT_PER_ENGAGEMENT = ['screeningRequired'];
// Client Group and Entity identify the engagement — an amendment cannot move it.
const EAR_FIXED_HINT = 'Fixed for a registered engagement — an amendment cannot change it';

/**
 * Diff the edited fields against the baseline → { field: newValue } carrying
 * only real changes. Both sides are in the engagement master's value shape, so
 * the keys are exactly the ones the EAR backend whitelists.
 */
export const diffEarFields = (baseline, fields, options) => {
  const next = {
    ...fieldsToEngagementValues(fields, options || {}),
    clientGroup: String(fields.clientGroup || '').trim() || null,
    entityId: fields.entityId != null ? fields.entityId : null,
  };
  const changes = {};
  const norm = (v) => (v === undefined || v === null || v === '' ? null : v);
  Object.keys(next).forEach((k) => {
    if (NOT_PER_ENGAGEMENT.includes(k)) return;
    if (ID_LIST_FIELDS.includes(k)) {
      const a = [...((baseline || {})[k] || [])].sort((x, y) => x - y);
      const b = [...(next[k] || [])].sort((x, y) => x - y);
      if (JSON.stringify(a) !== JSON.stringify(b)) changes[k] = next[k] || [];
      return;
    }
    if (String(norm((baseline || {})[k])) !== String(norm(next[k]))) {
      changes[k] = norm(next[k]);
    }
  });
  return changes;
};

/**
 * Prefill for the Modify modal: the live engagement, with the request's already
 * requested "after" values laid on top. Both are converted through the shared
 * engagement→fields mapper so the modal shows exactly what the editor writes.
 */
export const earPrefillFields = (engagement, payload, options) => {
  const merged = { ...(engagement || {}) };
  Object.entries(payload || {}).forEach(([field, diff]) => {
    if (field === 'teamStaffIds') merged.teamMembers = (diff.afterIds || []).map((id) => ({ staffId: id }));
    else if (field === 'partnerStaffIds') merged.partners = (diff.afterIds || []).map((id) => ({ staffId: id }));
    else if (field === 'directorStaffIds') merged.directors = (diff.afterIds || []).map((id) => ({ staffId: id }));
    else if (field === 'managerStaffIds') merged.managers = (diff.afterIds || []).map((id) => ({ staffId: id }));
    else merged[field] = diff.after;
  });
  return {
    ...engagementToFields(merged, options || {}),
    clientGroup: merged.clientGroup || '',
    entityId: merged.entityId != null ? merged.entityId : null,
  };
};

/**
 * Shared field editor for update-type Amendment requests — used by the
 * new-request page and the detail page's modify modal so both stay in sync.
 *
 * `anchor` maps a field key to the return-comment key the detail page uses
 * (`<engagementId>:<field>` on multi-engagement requests, the bare key on
 * single ones) so a reviewer's comment shows under the exact control it talks
 * about. The New page leaves it off — a brand-new request has no comments and
 * FormKit's `field=null` renders nothing.
 *
 * `engagement` is the live engagement behind this tab: once its QC6/QC7 is
 * approved (qcRecordId set) Estimated Fees locks — the QC figure governs and
 * corrections go through QC Amendment (the backend rejects the field with
 * FEES_LOCKED).
 *
 * The slot check lives HERE (like Registration's in EngRegFormFields), so the
 * New page and the Modify modal both ask it: once the tab moves the engagement
 * to another company / FYE / service or changes its description, the same
 * lookup the submit runs answers under Service Provided. `onDuplicates` tells
 * the page, which refuses a Submit already known to fail.
 */
export const EarFieldsEditor = ({ fields, setFields, options, currentName, engagement = null, anchor = () => null, onDuplicates = null }) => {
  const set = (k, v) => setFields((prev) => ({ ...prev, [k]: v }));
  const { duplicates, notices } = useEngagementSlotCheck('/api/workflow/ear/duplicate-check', engagement, fields);
  const reportRef = useRef(onDuplicates);
  reportRef.current = onDuplicates;
  useEffect(() => { if (reportRef.current) reportRef.current(duplicates); }, [duplicates]);
  const entities = options.entities || [];
  const selectedEntity = entities.find((en) => String(en.id) === String(fields.entityId));
  const entityLabel = selectedEntity
    ? `${selectedEntity.entityName}${selectedEntity.uen ? ` (${selectedEntity.uen})` : ''}`
    : '';

  // The blocks Registration numbers as sections, in Registration's order,
  // as titled sub-sections of this one numbered section — everything here is
  // "the requested change" of one engagement. Screening is not here: it is
  // answered once for the whole request, after the Reviewer.
  return (
    <>
      <SubSection title="Engagement" first>
        <Field label="Engagement Name" hint="Composed from Entity Name - UEN - FYE — it updates when those change.">
          <input type="text" value={currentName || ''} readOnly className={lockedInputCls} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Client Group" field={anchor('clientGroup')}>
            <LockTip text={EAR_FIXED_HINT} block>
              <input type="text" value={fields.clientGroup || '—'} readOnly className={lockedInputCls} />
            </LockTip>
          </Field>
          <Field label="Entity Name" field={anchor('entityName')}>
            <LockTip text={EAR_FIXED_HINT} block>
              <input type="text" value={entityLabel || '—'} readOnly className={lockedInputCls} />
            </LockTip>
          </Field>
        </div>
        {/* No Date of Appointment: it is the client master's and an amendment
            cannot change it. */}
        <EngagementDateFields f={fields} set={set} required={false} anchor={anchor} />
      </SubSection>
      <SubSection title="Service & Client Profile">
        <EngagementProfileFields
          f={fields} set={set} options={options} required={false} anchor={anchor} feesLocked={!!(engagement && engagement.qcRecordId)}
          descriptionNeeded={descriptionNeededFrom(duplicates, notices)}
          descriptionNotice={(
            <>
              <DuplicateEngagementWarning duplicates={duplicates} />
              <DescriptionOfServiceNotice duplicates={duplicates} notices={notices} />
            </>
          )}
        />
      </SubSection>
      <SubSection title="People" hint={EAR_PEOPLE_HINT}>
        <EngagementPeopleFields f={fields} set={set} options={options} required={false} showTeam={false} anchor={anchor} />
      </SubSection>
      <SubSection title="Remarks">
        <Field label="Remarks" field={anchor('remarks')}>
          <textarea rows="2" value={fields.remarks || ''} onChange={(e) => set('remarks', e.target.value)} className={inputCls} />
        </Field>
      </SubSection>
    </>
  );
};

// The request's Screening section (after the Reviewer, like Registration's):
// one answer for every engagement on the request, asked only when the
// reviewer is from the QM department; with anyone else only the requester's
// own document upload shows. Shared by the New page and the Modify modal.
export const EAR_SCREENING_SECTION_HINT = (
  <>The screening (background check) for the engagements on this request. <InfoTip text="Yes — answer the screening questions; approval opens a Screening request in which QM performs the check and files the document. No — you may attach your own screening document; approval files it on every engagement. Leave the question blank to keep the engagements' screening as it is." /></>
);
export const EAR_SCREENING_BLANK_NOTE = "Left blank, the engagements' screening stays as it is.";
export const EAR_SCREENING_DOC_ONLY_HINT = 'The reviewer is not from the QM department, so QM does not perform the screening. You may attach your own screening document; approval files it on every engagement.';
export const emptyEarScreening = () => ({ screeningRequired: '', ...emptyScreeningAnswers() });

// Prefill from a saved request's payload.screening.
export const earScreeningFromPayload = (payload) => {
  const entry = payload && payload.screening;
  if (!entry || typeof entry.required !== 'boolean') return emptyEarScreening();
  const a = entry.answers || {};
  const tri = (v) => (v === true ? 'yes' : v === false ? 'no' : '');
  return {
    screeningRequired: entry.required ? 'yes' : 'no',
    ultimateParentHqCity: a.ultimateParentHqCity || '',
    ubo: a.ubo || '',
    isTne: tri(a.isTne),
    tneBasis: a.tneBasis || '',
    isTna: tri(a.isTna),
    tnaBasis: a.tnaBasis || '',
  };
};

// The answer + six answers + the requester's own document, onto a FormData.
// Not asked (reviewer outside QM) sends a blank answer and only the document.
export const appendEarScreening = (form, screening, screeningFile, asked) => {
  const answer = asked ? screening.screeningRequired : '';
  form.set('screeningRequired', answer === 'yes' ? 'true' : answer === 'no' ? 'false' : '');
  if (answer === 'yes') appendScreeningAnswers(form, screening);
  if ((answer === 'no' || !asked) && screeningFile) form.append('screening', screeningFile);
  return form;
};

export const earScreeningGaps = (screening, asked) =>
  (asked && screening.screeningRequired === 'yes' ? screeningRequiredGaps(screening) : []);

// ── TAB 版 ────────────────────────────────────────────────────────────────
// 一張單涵蓋同集團、FYE 同年的多個案，**每案一個頁籤、各自修改**，審核一併。
// 頁籤＝分段藥丸（active 白底浮起），琥珀徽章顯示該案改了幾項、沒動的一眼
// 看得出來；同公司多案時名字下多一行服務／描述，名字過長換行。TabBar 供 Detail 的 Modify modal
// 共用。
// `className` 給不自帶子元素間距的容器用（審核頁的 SectionCard；New 頁的
// FormSection 與 Modify modal 的 FormModal 都有 space-y-4，不必傳）。
export const EarTabBar = ({ items, activeId, onSelect, className = '' }) => (
  <div className={`${companyTabBar} ${className}`}>
    {items.map(({ id, label, sub, badge, title }) => (
      <button
        key={id}
        type="button"
        onClick={() => onSelect(id)}
        title={title || label}
        className={`${companyTab} ${activeId === id ? companyTabActive : companyTabIdle}`}
      >
        <CompanyTabName label={label} sub={sub} />
        {/* badge undefined＝純切換用（例如審核頁的資訊卡），不畫改動徽章。 */}
        {badge !== undefined && (badge > 0 ? (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800" title={`${badge} change${badge === 1 ? '' : 's'} on this tab`}>
            {badge}
          </span>
        ) : (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-slate-200 text-slate-500" title="No changes on this tab — approval will not change this company">no changes</span>
        ))}
      </button>
    ))}
  </div>
);

// 分頁只改自己那一家、未動過的分頁核准後不會有任何變更——填寫當下就講。
// 這是說明不是警告：純灰字、沒有底色外框（琥珀留給要人處理的事——撞格、
// 送出前逐家確認裡的「no changes」）。New 頁放在②的標題提示，Modify
// modal 沒有段落標題，放在頁籤列上方（EarPerTabHint）。
export const earPerTabHintText = (count) => (count > 1
  ? 'One tab per company — each tab edits only its own company; a tab marked “no changes” stays as it is.'
  : 'One tab per company — each tab edits only its own company.');
export const EarPerTabHint = ({ count }) => (
  <p className="text-xs text-slate-500">{earPerTabHintText(count)}</p>
);

// 送出前的逐家確認（多公司時才出現；自製 modal，禁原生 confirm）：逐家列出
// 「這家會改幾項」，0 變更的公司醒目警示「核准後不會有任何改變」——填寫者
// 必須看過這張清單才送得出去。
const EarSubmitConfirmModal = ({ rows, addRows, saving, onBack, onConfirm }) => (
  <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
      <div className="flex items-center justify-between p-6 border-b border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900">Confirm the Changes per Company</h3>
        <button type="button" onClick={onBack} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6 space-y-4">
        <p className="text-sm text-slate-700">
          Each company only receives the changes made on <strong>its own tab</strong>. Review this summary before submitting:
        </p>
        <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 break-words text-slate-700" title={r.title}>
                {r.label}
                {r.sub && <span className="block text-xs text-slate-400">{r.sub}</span>}
              </span>
              {r.count > 0 ? (
                <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                  {r.count} change{r.count === 1 ? '' : 's'}
                </span>
              ) : (
                <span className="shrink-0 text-xs font-medium text-amber-700">
                  No changes — approval will not change this company
                </span>
              )}
            </li>
          ))}
          {addRows.map((name) => (
            <li key={`add-${name}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="min-w-0 break-words text-slate-700">{name}</span>
              <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">New engagement will be created</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onBack} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Back to Editing</button>
          <button type="button" onClick={onConfirm} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Submitting…' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// 「讓 B 跟 A 一模一樣」：把來源頁籤**目前的整份內容**
// 帶到目前頁籤——不必先編輯過來源（若只抄「來源改過的欄位」，兩案
// 原值本來就不同時什麼都抄不到）。公司身分（entity）永不複製；文件槽綁各案
// 的檔案，也不複製。徽章隨即顯示目前頁籤因此與自己現值差幾項，抄了什麼
// 一目瞭然。
const NON_COPY_FIELDS = ['clientGroup', 'entityId', 'entityName', 'screeningRequired'];
export const copyEarFields = (srcTab, dstFields, { feesLocked = false } = {}) => {
  const merged = { ...dstFields };
  for (const [k, v] of Object.entries(srcTab.fields || {})) {
    if (NON_COPY_FIELDS.includes(k)) continue;
    // 目的案的 QC 已核准＝Estimated Fees（金額＋幣別）以 QC 為準——抄過去也
    // 送不出（後端 FEES_LOCKED），直接不抄。
    if ((k === 'proposedFees' || k === 'proposedFeesCcy') && feesLocked) continue;
    // Same for the PIE answer: the approved QC owns it (PIE_LOCKED).
    if ((k === 'isPie' || k === 'pieType' || k === 'pieTypeOther') && feesLocked) continue;
    merged[k] = Array.isArray(v) ? [...v] : v;
  }
  return { fields: merged };
};

// 複製列（New 與 Modify 共用）：**其他每個頁籤都是來源**（括號＝該籤自己的
// 改動數，0 不顯示——沒編輯過也抄得動，抄的是它現在的內容）。
export const EarCopyRow = ({ sources, onCopy }) => {
  if (sources.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
      <span>Copy from:</span>
      {sources.map(({ id, label, badge }) => (
        <button
          key={id}
          type="button"
          onClick={() => onCopy(id)}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-300 bg-white text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
          title={`Make this tab match ${label}'s current values (company identity and documents stay)`}
        >
          <span className="break-words text-left">{label}</span>
          {badge > 0 && <span className="text-[10px] text-amber-700">({badge})</span>}
        </button>
      ))}
    </div>
  );
};

/**
 * Two tabs of this request landing in the same slot (company + FYE + service)
 * — e.g. both changed to the same service. The server refuses it too
 * (src/pages/earSlotConflict.js, one rule); the form says so as soon as it
 * happens. New page and Modify modal: `tabs` = { [id]: { baseline, fields } }.
 * @returns null or { code, message }
 */
export const earTabsSlotConflict = (engagementIds, tabs, engOf, options) => {
  const lower = (v) => String(v || '').trim().toLowerCase();
  const labels = engagementLabelsOf(engagementIds.map(engOf));
  const items = engagementIds.filter((id) => tabs[id]).map((id) => {
    const { baseline = {}, fields } = tabs[id];
    const after = fieldsToEngagementValues(fields, options || {});
    const entityId = fields.entityId != null ? fields.entityId : baseline.entityId;
    return {
      name: (labels[id] || {}).text || `#${id}`,
      entityId,
      yearEnd: after.yearEnd,
      service: after.serviceProvided,
      description: after.descriptionOfService,
      moved: String(entityId) !== String(baseline.entityId)
        || String(after.yearEnd || '') !== String(baseline.yearEnd || '')
        || lower(after.serviceProvided) !== lower(baseline.serviceProvided),
    };
  });
  const multi = new Set(((options || {}).multiFyeServices || []).map(lower));
  return earSlotConflict(items, (service) => multi.has(lower(service)));
};

export const EarSlotConflictNotice = ({ conflict }) => (conflict ? (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">{conflict.message}</div>
) : null);

// 短名：組合案名「Entity - UEN - FYE…」取公司段當頁籤標籤。
// 委派給 CoverageDisplay 的同一把拆解器（公司名含「 - 」也拆得對），
// 名字保留——EAR 的頁籤/差異列一直叫它 earShortName。
export const earShortName = shortEngagementName;

const WorkflowEarNew = () => {
  const navigate = useNavigate();

  const [options, setOptions] = useState({});
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // 涵蓋案（同集團＋FYE 同年，分組選單多選）＋每案自己的頁籤狀態。
  const [engagementIds, setEngagementIds] = useState([]);
  const [tabs, setTabs] = useState({});   // { [id]: { baseline, fields, engAttachments, attachmentUpdates } }
  const [activeId, setActiveId] = useState(null);
  const [reason, setReason] = useState('');
  const [reviewerStaffId, setReviewerStaffId] = useState('');
  const [files, setFiles] = useState([]);
  const [screening, setScreening] = useState(emptyEarScreening);
  const [screeningFile, setScreeningFile] = useState(null);
  const setScreeningField = (k, v) => setScreening((prev) => ({ ...prev, [k]: v }));
  const screeningAsked = useIsQmReviewer(reviewerStaffId);
  // 加同集團公司：每間 { entityId, file }。
  const [addCompanies, setAddCompanies] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        setOptions(await fetchJSON('/api/workflow/ear/options'));
      } catch (e) { setError(e.message); }
    })();
  }, []);

  const engOf = (id) => (options.engagements || []).find((e) => e.id === id) || null;
  // 公司名；同公司多案時加服務／描述（頁籤第二行、句子裡的括號）。
  const labels = engagementLabelsOf(engagementIds.map(engOf));
  const nameOf = (id) => (labels[id] || {}).text || `#${id}`;
  const patchTab = (id, part) => setTabs((prev) => ({ ...prev, [id]: { ...prev[id], ...part } }));

  // 選案變動：新案開頁籤（現值預填＋抓附件），移除的丟頁籤。
  const updateSelection = (ids) => {
    setError(null);
    setEngagementIds(ids);
    setTabs((prev) => {
      const next = {};
      for (const id of ids) {
        if (prev[id]) { next[id] = prev[id]; continue; }
        const en = engOf(id);
        if (!en) continue;
        next[id] = {
          baseline: engagementToEarValues(en),
          fields: earPrefillFields(en, null, options),
          engAttachments: [],
          attachmentUpdates: [],
        };
        (async () => {
          try {
            const a = await fetchJSON(`/api/workflow/ear/engagement/${id}/attachments`);
            patchTab(id, { engAttachments: (a.data || []).filter((f) => !f.superseded) });
          } catch { /* 附件槽顯示不出現況而已 */ }
        })();
      }
      return next;
    });
    setActiveId((cur) => (ids.includes(cur) ? cur : (ids[0] || null)));
    // 錨（第一案）換了就把加開公司歸零——加開的定義是「跟專案同集團」。
    if (ids[0] !== engagementIds[0]) setAddCompanies([]);
  };

  const changesOf = (id) => {
    const t = tabs[id];
    return t ? diffEarFields(t.baseline, t.fields, options) : {};
  };
  const badgeOf = (id) => Object.keys(changesOf(id)).length + ((tabs[id] || {}).attachmentUpdates || []).length;

  // 「讓 B 跟 A 一模一樣」：其他每個頁籤都可當來源，
  // 抄它目前的整份內容（不必先編輯過）。
  const [copyNote, setCopyNote] = useState(null);
  const copySources = engagementIds
    .filter((id) => id !== activeId && tabs[id])
    .map((id) => ({
      id,
      label: nameOf(id),
      badge: Object.keys(changesOf(id)).length,
    }));
  const copyFrom = (srcId) => {
    const src = tabs[srcId];
    const dst = tabs[activeId];
    if (!src || !dst) return;
    const copied = copyEarFields(src, dst.fields, { feesLocked: !!(engOf(activeId) || {}).qcRecordId });
    patchTab(activeId, { fields: copied.fields });
    setCopyNote(`This tab now matches ${nameOf(srcId)}'s values — the badge shows what differs from this engagement's current data. Company identity and documents are not copied.`);
  };
  useEffect(() => { setCopyNote(null); }, [activeId]);

  // 加公司候選：專案（第一案）entity 同集團的其他公司。
  const anchorEng = engagementIds.length > 0 ? engOf(engagementIds[0]) : null;
  const anchorEntity = anchorEng && anchorEng.entityId
    ? (options.entities || []).find((en) => en.id === anchorEng.entityId) || null
    : null;
  const coveredEntityIds = new Set(engagementIds.map((id) => (engOf(id) || {}).entityId).filter(Boolean));

  // 已在這個 FYE 有 engagement 的公司**不列**
  // ——問登記單的 duplicate-check（與後端擋重複同一支查詢，
  // 涵蓋非成員的案，前端自己的 feed 看不到那些）。null＝還在問，先不畫這段。
  const [takenEntityIds, setTakenEntityIds] = useState(null);
  useEffect(() => {
    if (!anchorEntity || !anchorEng || !anchorEng.yearEnd) { setTakenEntityIds(null); return undefined; }
    const candidates = (options.entities || [])
      .filter((en) => en.clientGroupId === anchorEntity.clientGroupId)
      .map((en) => en.id);
    if (candidates.length === 0) { setTakenEntityIds(new Set()); return undefined; }
    let cancelled = false;
    setTakenEntityIds(null);
    (async () => {
      try {
        // The slot is company + FYE + service: without the service the
        // lookup has nothing to say. An added company inherits the anchor's
        // service and description.
        const qs = new URLSearchParams({
          entityIds: candidates.join(','), yearEnd: anchorEng.yearEnd,
          service: anchorEng.serviceProvided || '', description: anchorEng.descriptionOfService || '',
        });
        const res = await fetchJSON(`/api/workflow/ear/duplicate-check?${qs.toString()}`);
        if (!cancelled) setTakenEntityIds(new Set((res.duplicates || []).map((d) => d.entityId)));
      } catch {
        // 問不到就不過濾——後端提交時仍是權威。
        if (!cancelled) setTakenEntityIds(new Set());
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorEntity && anchorEntity.id, anchorEng && anchorEng.yearEnd, anchorEng && anchorEng.serviceProvided, options.entities]);

  const addChoices = anchorEntity && takenEntityIds !== null
    ? (options.entities || []).filter((en) =>
        en.clientGroupId === anchorEntity.clientGroupId
        && !coveredEntityIds.has(en.id)
        && !takenEntityIds.has(en.id))
    : [];
  const toggleAddCompany = (entityId) => setAddCompanies((prev) =>
    prev.some((c) => c.entityId === entityId)
      ? prev.filter((c) => c.entityId !== entityId)
      : [...prev, { entityId, file: null, fee: '' }]);
  const setAddCompanyFile = (entityId, file) => setAddCompanies((prev) =>
    prev.map((c) => (c.entityId === entityId ? { ...c, file } : c)));
  // Each added company's own Estimated Fees — mandatory, never copied from
  // this engagement (its figure is not the new company's).
  const setAddCompanyFee = (entityId, fee) => setAddCompanies((prev) =>
    prev.map((c) => (c.entityId === entityId ? { ...c, fee } : c)));

  // The blanks this form can have — the coverage, the reason and the
  // reviewer. Everything else here is a rule about the change set (a Partner
  // must remain, nothing changed yet, a new company without its BizFile) and
  // stays a banner error.
  const gaps = useMemo(() => {
    const g = [];
    if (engagementIds.length === 0) g.push({ key: 'engagementIds', label: 'Engagement(s)' });
    if (!reason.trim()) g.push({ key: 'reason', label: 'Reason' });
    if (!reviewerStaffId) g.push({ key: 'reviewerName', label: 'Reviewer' });
    g.push(...earScreeningGaps(screening, screeningAsked));
    return g;
  }, [engagementIds, reason, reviewerStaffId, screening, screeningAsked]);
  const validation = useFormValidation(gaps);

  // 送出前確認（多公司時）：{ form, rows, addRows }。
  const [confirming, setConfirming] = useState(null);

  // 驗證＋組 FormData；規則錯誤 setError 後回 null。
  const buildSubmission = () => {
    const clash = earTabsSlotConflict(engagementIds, tabs, engOf, options);
    if (clash) { setError(clash.message); return null; }
    const dupTab = engagementIds.find((id) => ((tabs[id] || {}).duplicates || []).length > 0);
    if (dupTab) { setError(tabs[dupTab].duplicates[0].message); return null; }
    const amendments = [];
    let anyChange = false;
    const allUpdates = [];
    for (const id of engagementIds) {
      const t = tabs[id];
      if (!t) continue;
      const changes = changesOf(id);
      const name = nameOf(id);
      if (changes.partnerStaffIds !== undefined && (changes.partnerStaffIds || []).length === 0) {
        setError(`${name}: a Partner in charge must remain on the engagement.`); return null;
      }
      if (changes.managerStaffIds !== undefined && (changes.managerStaffIds || []).length === 0) {
        setError(`${name}: at least one Manager must remain on the engagement.`); return null;
      }
      if (Object.keys(changes).length > 0 || t.attachmentUpdates.length > 0) anyChange = true;
      amendments.push({ engagementId: id, changes });
      for (const e of t.attachmentUpdates) allUpdates.push({ ...e, engagementId: id });
    }
    const screeningAnswered = screeningAsked ? screening.screeningRequired !== '' : !!screeningFile;
    if (!anyChange && addCompanies.length === 0 && !screeningAnswered) {
      setError('No changes made yet — edit a field on a tab, add an attachment update or add a company.'); return null;
    }
    const missingBizfile = addCompanies.filter((c) => !c.file);
    if (missingBizfile.length > 0) {
      const names = missingBizfile
        .map((c) => (addChoices.find((en) => en.id === c.entityId) || {}).entityName || `#${c.entityId}`);
      setError(`Attach the BizFile PDF for: ${names.join(', ')}.`); return null;
    }
    const missingFee = addCompanies.filter((c) => String(c.fee ?? '').trim() === '');
    if (missingFee.length > 0) {
      const names = missingFee
        .map((c) => (addChoices.find((en) => en.id === c.entityId) || {}).entityName || `#${c.entityId}`);
      setError(`Enter the Estimated Fees for: ${names.join(', ')}.`); return null;
    }

    const form = new FormData();
    form.set('requestType', 'update');
    form.set('reason', reason.trim());
    form.set('reviewerStaffId', reviewerStaffId);
    form.set('amendments', JSON.stringify(amendments));
    if (allUpdates.length > 0) {
      const meta = allUpdates.map((e) => ({
        engagementId: e.engagementId,
        docType: e.docType || null,
        ...(e.replacesSource ? { replacesSource: e.replacesSource, replacesFileId: e.replacesFileId } : {}),
      }));
      allUpdates.forEach((e) => form.append('attachmentFiles', e.file));
      form.set('attachmentMeta', JSON.stringify(meta));
    }
    if (addCompanies.length > 0) {
      form.set('addEntityIds', JSON.stringify(addCompanies.map((c) => c.entityId)));
      form.set('addEntityFees', JSON.stringify(Object.fromEntries(addCompanies.map((c) => [c.entityId, String(c.fee).trim()]))));
      addCompanies.forEach((c) => form.append(`bizfile_${c.entityId}`, c.file));
    }
    files.forEach((f) => form.append('files', f));
    appendEarScreening(form, screening, screeningFile, screeningAsked);

    const rows = engagementIds.map((id) => {
      const eng = engOf(id) || {};
      return {
        id,
        label: (labels[id] || {}).label || `#${id}`,
        sub: (labels[id] || {}).sub,
        title: eng.engagementName,
        count: badgeOf(id),
      };
    });
    const addRows = addCompanies
      .map((c) => (addChoices.find((en) => en.id === c.entityId) || {}).entityName || `#${c.entityId}`);
    return { form, rows, addRows };
  };

  const doPost = async (form) => {
    setSaving(true);
    try {
      const data = await fetchJSON('/api/workflow/ear', { method: 'POST', body: form });
      navigate(`/workflow/ear/${data.request.id}`);
    } catch (e) { setError(e.message); setConfirming(null); }
    finally { setSaving(false); }
  };

  const submit = () => {
    setError(null);
    if (!validation.validate()) return;
    const built = buildSubmission();
    if (!built) return;
    // 單一公司沒有分頁陷阱，直接送；多公司先看逐家確認清單。
    if (engagementIds.length > 1) setConfirming(built);
    else doPost(built.form);
  };

  const hasAddSection = engagementIds.length > 0 && anchorEntity && addChoices.length > 0;
  const slotConflict = earTabsSlotConflict(engagementIds, tabs, engOf, options);
  const active = activeId ? tabs[activeId] : null;
  const activeEng = activeId ? engOf(activeId) : null;

  return (
    <FormShell
      backTo="/workflow/ear"
      backLabel="Engagement Amendment"
      title="New Engagement Amendment Request"
      subtitle="Change the details of an existing engagement."
      error={error || validation.error}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={engagementBlockReasons(engagementIds.map(engOf).filter(Boolean), 'ear').length > 0}
    >
      <FormValidationProvider value={validation.providerValue}>
        <FormSection step={1} title="Select the Engagement(s)" hint={<>Only engagements you are a member of are listed, grouped by client group and FYE. <InfoTip text="Pick several companies of the same project to amend them in one request. New engagements go through Engagement Registration; CaseWare access / charge codes go through the CaseWare, Charge Code & Team Update flow." /></>}>
          <GroupedEngagementPicker
            engagements={options.engagements || []}
            selectedIds={engagementIds}
            onChange={updateSelection}
          />
          <FieldInvalidInline field="engagementIds" text="Required — pick at least one engagement" />
          <EngagementBlockers selected={engagementIds.map(engOf).filter(Boolean)} flow="ear" />
        </FormSection>

        <FormSection step={2} title="Requested Changes" hint={<>{earPerTabHintText(engagementIds.length)} <InfoTip text="The amber badge counts the changes on that tab. The whole request is reviewed and approved together." /></>}>
          {engagementIds.length === 0 ? (
            <p className="text-sm text-slate-500">Select the engagement(s) above first.</p>
          ) : (
            <>
              {engagementIds.length > 1 && (
                <>
                  <EarTabBar
                    items={engagementIds.map((id) => ({
                      id,
                      label: (labels[id] || {}).label || `#${id}`,
                      sub: (labels[id] || {}).sub,
                      title: (engOf(id) || {}).engagementName,
                      badge: badgeOf(id),
                    }))}
                    activeId={activeId}
                    onSelect={setActiveId}
                  />
                </>
              )}
              {engagementIds.length > 1 && (
                <EarCopyRow sources={copySources} onCopy={copyFrom} />
              )}
              {copyNote && (
                <p className="text-xs text-emerald-700">{copyNote}</p>
              )}
              <EarSlotConflictNotice conflict={slotConflict} />
              {active && activeEng && (
                <EarTabPanel
                  key={activeId}
                  engagement={activeEng}
                  tab={active}
                  setTab={(part) => patchTab(activeId, part)}
                  options={options}
                />
              )}
            </>
          )}
        </FormSection>

        {/* 加同集團公司：核准時逐間建案，FYE／服務／
            人員／團隊／客戶檔案全繼承專案第一案；每間必附 BizFile PDF（比照登
            記）。 */}
        {hasAddSection && (
          <FormSection
            step={3}
            title="Add Companies"
            hint={<>Tick other companies of the same client group; attach each company's BizFile PDF and enter its Estimated Fees. <InfoTip text="Their engagements are created when this request is approved. Each new engagement inherits the project's FYE, service, people and client profile — except the Estimated Fees, which you enter for each company here." /></>}
          >
            <div className="space-y-2">
              {addChoices.map((en) => {
                const picked = addCompanies.find((c) => c.entityId === en.id) || null;
                return (
                  <div key={en.id} className="border border-slate-200 rounded-lg px-3 py-2">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={!!picked} onChange={() => toggleAddCompany(en.id)} />
                      {en.entityName}{en.uen ? <span className="text-xs text-slate-500">({en.uen})</span> : <span className="text-xs text-amber-600">(no UEN — record it in Clients first)</span>}
                    </label>
                    {picked && (
                      <div className="mt-1.5 pl-6 flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-slate-500">BizFile<span className="text-red-500"> *</span> · PDF</span>
                        <FilePicker accept=".pdf"
                          onChange={(ev) => { setAddCompanyFile(en.id, ev.target.files?.[0] || null); ev.target.value = ''; }}
                          files={picked.file || null} />
                      </div>
                    )}
                    {picked && (
                      <div className="mt-1.5 pl-6 flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-slate-500">Estimated Fees<span className="text-red-500"> *</span></span>
                        <span className="text-slate-500">{(engOf(engagementIds[0]) || {}).proposedFeesCcy || 'SGD'}</span>
                        <input
                          type="number" min="0" step="0.01"
                          value={picked.fee || ''}
                          onChange={(ev) => setAddCompanyFee(en.id, ev.target.value)}
                          aria-label={`Estimated Fees — ${en.entityName}`}
                          className="w-40 px-2 py-1 border border-slate-300 rounded-md text-sm"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </FormSection>
        )}

        <FormSection step={hasAddSection ? 4 : 3} title="Attachments">
          <Field label="Supporting Documents">
            <FilePicker
              multiple
              accept=".pdf,.doc,.docx"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </Field>
        </FormSection>

        <FormSection step={hasAddSection ? 5 : 4} title="Reason">
          <Field label="Reason" required field="reason">
            <textarea rows="2" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
          </Field>
        </FormSection>

        <FormSection step={hasAddSection ? 6 : 5} title="Reviewer">
          <Field label="Reviewer" required field="reviewerName" hint={ENGREG_REVIEWER_HINT}>
            <ReviewerSelect roster="master" value={reviewerStaffId} onChange={setReviewerStaffId} />
          </Field>
        </FormSection>

        {/* After the Reviewer, like Registration: the question only with a
            reviewer from the QM department, otherwise just the upload. */}
        {reviewerStaffId && (
          <FormSection step={hasAddSection ? 7 : 6} title="Screening" hint={screeningAsked ? EAR_SCREENING_SECTION_HINT : EAR_SCREENING_DOC_ONLY_HINT}>
            <ScreeningUpload
              f={screening} set={setScreeningField}
              screeningFile={screeningFile} setScreeningFile={setScreeningFile}
              anchor={(k) => k} required={false} blankNote={EAR_SCREENING_BLANK_NOTE}
              docOnly={!screeningAsked}
            />
          </FormSection>
        )}
      </FormValidationProvider>
      {confirming && (
        <EarSubmitConfirmModal
          rows={confirming.rows}
          addRows={confirming.addRows}
          saving={saving}
          onBack={() => setConfirming(null)}
          onConfirm={() => doPost(confirming.form)}
        />
      )}
    </FormShell>
  );
};

// 一個頁籤＝一個案：欄位編輯器＋文件槽（BizFile／FS／QC35 新版）。
const EarTabPanel = ({ engagement, tab, setTab, options }) => {
  const nextUid = useRef(1);
  const SLOT_LABELS = { BIZFILE: 'BizFile', FS: 'Signed Financial Statements', QC35: 'QC35 Form' };
  const originLabel = (f) => (
    f.source === 'engreg' ? 'Registration' : f.source === 'fs' ? 'Signed Financial Statements'
      : f.source === 'qc35' ? 'QC35 Form' : f.source === 'group' ? 'Client group'
        : f.source === 'own' ? 'Uploaded' : f.source
  );
  const currentForSlot = (dt) => tab.engAttachments.find((f) => !f.referenced && (
    (f.source === 'own' && f.docType === dt) ||
    (f.source === 'engreg' && f.docType === dt) ||
    (dt === 'FS' && f.source === 'fs' && f.docType === 'fs') ||
    (dt === 'QC35' && f.source === 'qc35')
  ));
  const slotDefs = [
    { dt: 'BIZFILE', required: false, always: true },
    { dt: 'FS', required: false },
    { dt: 'QC35', required: false },
  ];
  const slots = slotDefs.filter((s) => s.required || s.always || currentForSlot(s.dt));

  const slotEntry = (dt) => tab.attachmentUpdates.find((e) => e.slot === dt);
  const setSlotFile = (dt, file, current) => {
    const link = current && !current.referenced;
    const others = tab.attachmentUpdates.filter((e) => e.slot !== dt);
    setTab({
      attachmentUpdates: !file ? others : [...others, {
        uid: nextUid.current++, slot: dt, file, docType: dt,
        replacesSource: link ? current.source : '',
        replacesFileId: link ? String(current.fileId) : '',
      }],
    });
  };

  const SlotList = ({ slots: list }) => (
    <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
      {list.map(({ dt, required }) => {
        const current = currentForSlot(dt);
        const entry = slotEntry(dt);
        return (
          <li key={dt} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-700">
                {SLOT_LABELS[dt] || dt}{required && <span className="text-red-500"> *</span>}
              </div>
              <div className="text-xs mt-0.5">
                {entry ? (
                  <span className="text-emerald-700">→ {entry.file.name} (new version)</span>
                ) : current ? (
                  <span className="text-slate-500">On file: {current.originalName}<span className="text-slate-500"> · {originLabel(current)}</span></span>
                ) : (
                  <span className="text-slate-400">— not on file —</span>
                )}
              </div>
            </div>
            {entry ? (
              <button type="button" onClick={() => setSlotFile(dt, null)} className="text-slate-400 hover:text-red-600 shrink-0">
                <X className="w-4 h-4" />
              </button>
            ) : (
              <FilePicker accept=".pdf,.doc,.docx" showSelection={false}
                buttonLabel={current ? 'Upload New Version' : `Upload ${SLOT_LABELS[dt] || dt}`}
                onChange={(ev) => { setSlotFile(dt, ev.target.files?.[0], current); ev.target.value = ''; }} />
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      <EarFieldsEditor
        fields={tab.fields}
        setFields={(updater) => setTab({ fields: typeof updater === 'function' ? updater(tab.fields) : updater })}
        options={options}
        currentName={engagement.engagementName}
        engagement={engagement}
        onDuplicates={(duplicates) => setTab({ duplicates })}
      />

      {/* BizFile is its own block, like Registration's; the other named
          documents (a new version of the Signed FS / QC35) follow only when
          the engagement holds one. */}
      <SubSection title="BizFile">
        <SlotList slots={slots.filter((sl) => sl.dt === 'BIZFILE')} />
      </SubSection>
      {slots.some((sl) => sl.dt !== 'BIZFILE') && (
        <SubSection title="Other Documents">
          <SlotList slots={slots.filter((sl) => sl.dt !== 'BIZFILE')} />
        </SubSection>
      )}
    </>
  );
};

export default WorkflowEarNew;
